import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Loader2, CheckCircle2, ArrowRight } from "lucide-react";
import { Seo } from "@/components/Seo";
import { FadeIn, Overline } from "@/components/Reveal";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CATEGORIES = ["Campaign enquiry", "Report abuse or impersonation", "General enquiry"];

const initial = {
  name: "",
  email: "",
  company: "",
  website: "",
  category: "Campaign enquiry",
  objectives: "",
  additional: "",
  nickname: "",
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function Contact() {
  const [form, setForm] = useState(initial);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (k) => (e) => {
    const v = e?.target ? e.target.value : e;
    setForm((f) => ({ ...f, [k]: v }));
    setErrors((er) => ({ ...er, [k]: undefined }));
  };

  const validate = () => {
    const er = {};
    if (form.name.trim().length < 2) er.name = "Please enter your name.";
    if (!EMAIL_RE.test(form.email.trim())) er.email = "Please enter a valid work email.";
    if (form.company.trim().length < 2) er.company = "Please enter your company.";
    if (form.objectives.trim().length < 10)
      er.objectives = "Tell us a little about your campaign objectives (at least 10 characters).";
    setErrors(er);
    return Object.keys(er).length === 0;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, {
        ...form,
        website: form.website.trim() || null,
        additional: form.additional.trim() || null,
      });
      setSent(true);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg =
        typeof detail === "string"
          ? detail
          : "Something went wrong sending your enquiry. Please try again, or email admin@symmori.com directly.";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const fieldCls =
    "rounded-none border-black/15 bg-white focus-visible:ring-0 focus-visible:border-mutedblue h-12";
  const areaCls =
    "rounded-none border-black/15 bg-white focus-visible:ring-0 focus-visible:border-mutedblue min-h-[120px]";

  return (
    <>
      <Seo
        title="Contact Symmori — Discuss a YouTube Sponsorship Campaign"
        description="Tell Symmori about your brand and campaign objectives. We'll explore whether informational YouTube is the right fit for your sponsorship."
        path="/contact"
      />
      <section className="pt-40 pb-24 md:pt-48 md:pb-32 grain relative" data-testid="contact-page">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-16">
          <div className="md:col-span-5">
            <FadeIn>
              <Overline>Contact</Overline>
              <h1 className="mt-6 font-serif text-4xl md:text-5xl lg:text-6xl font-medium tracking-tight leading-tight">
                Discuss a <em className="italic text-mutedblue">campaign</em>.
              </h1>
              <p className="mt-8 text-base md:text-lg text-charcoal/75 leading-relaxed max-w-md">
                Tell us about your brand, your objectives and the audiences you want
                to reach. We review every enquiry personally and respond where we
                believe informational YouTube is a genuine fit.
              </p>

              <div className="mt-12 border-t border-black/10 pt-8">
                <p className="text-xs uppercase tracking-[0.2em] font-semibold text-mutedblue">
                  Prefer email?
                </p>
                <a
                  href="mailto:admin@symmori.com"
                  data-testid="contact-email-link"
                  className="mt-4 inline-flex items-center gap-3 text-ink underline underline-offset-8 decoration-ink/30 hover:decoration-mutedblue transition-colors duration-300"
                >
                  <Mail className="h-4 w-4" aria-hidden="true" />
                  admin@symmori.com
                </a>
              </div>

              <p className="mt-12 text-xs text-charcoal/60 leading-relaxed max-w-md">
                Privacy notice: the information you submit is used only to respond to
                your enquiry and manage our correspondence. It is stored securely,
                never sold, and handled as described in our{" "}
                <a href="/privacy-policy" className="underline underline-offset-4 decoration-ink/30 hover:decoration-mutedblue">
                  Privacy Policy
                </a>
                .
              </p>
            </FadeIn>
          </div>

          <div className="md:col-span-7">
            <FadeIn delay={0.15}>
              {sent ? (
                <div
                  className="bg-white border border-black/10 p-10 md:p-14 text-center"
                  data-testid="contact-success"
                >
                  <CheckCircle2 className="h-10 w-10 text-mutedblue mx-auto" aria-hidden="true" />
                  <h2 className="mt-6 font-serif text-3xl tracking-tight">Thank you.</h2>
                  <p className="mt-4 text-sm md:text-base text-charcoal/70 leading-relaxed max-w-md mx-auto">
                    Your enquiry has been received. We review every message and will
                    be in touch where there's a strong fit.
                  </p>
                  <button
                    onClick={() => {
                      setForm(initial);
                      setSent(false);
                    }}
                    data-testid="contact-send-another"
                    className="mt-8 text-sm underline underline-offset-8 decoration-ink/30 hover:decoration-mutedblue transition-colors duration-300"
                  >
                    Send another enquiry
                  </button>
                </div>
              ) : (
                <form
                  onSubmit={submit}
                  noValidate
                  className="bg-white border border-black/10 p-8 md:p-12 space-y-8"
                  data-testid="contact-form"
                >
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                    <div className="space-y-2">
                      <Label htmlFor="cf-name">Name *</Label>
                      <Input
                        id="cf-name"
                        data-testid="contact-name-input"
                        value={form.name}
                        onChange={set("name")}
                        className={fieldCls}
                        autoComplete="name"
                      />
                      {errors.name && <p className="text-xs text-red-700" role="alert">{errors.name}</p>}
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="cf-email">Work email *</Label>
                      <Input
                        id="cf-email"
                        type="email"
                        data-testid="contact-email-input"
                        value={form.email}
                        onChange={set("email")}
                        className={fieldCls}
                        autoComplete="email"
                      />
                      {errors.email && <p className="text-xs text-red-700" role="alert">{errors.email}</p>}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                    <div className="space-y-2">
                      <Label htmlFor="cf-company">Company *</Label>
                      <Input
                        id="cf-company"
                        data-testid="contact-company-input"
                        value={form.company}
                        onChange={set("company")}
                        className={fieldCls}
                        autoComplete="organization"
                      />
                      {errors.company && <p className="text-xs text-red-700" role="alert">{errors.company}</p>}
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="cf-website">Website (optional)</Label>
                      <Input
                        id="cf-website"
                        data-testid="contact-website-input"
                        value={form.website}
                        onChange={set("website")}
                        className={fieldCls}
                        placeholder="https://"
                        inputMode="url"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="cf-category">Nature of enquiry</Label>
                    <Select value={form.category} onValueChange={set("category")}>
                      <SelectTrigger
                        id="cf-category"
                        data-testid="contact-category-select"
                        className="rounded-none border-black/15 bg-white focus:ring-0 focus:border-mutedblue h-12"
                      >
                        <SelectValue placeholder="Select a category" />
                      </SelectTrigger>
                      <SelectContent className="rounded-none">
                        {CATEGORIES.map((c) => (
                          <SelectItem key={c} value={c} data-testid={`contact-category-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                            {c}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="cf-objectives">Campaign objectives *</Label>
                    <Textarea
                      id="cf-objectives"
                      data-testid="contact-objectives-input"
                      value={form.objectives}
                      onChange={set("objectives")}
                      className={areaCls}
                      placeholder="What are you hoping to achieve, and who are you trying to reach?"
                    />
                    {errors.objectives && (
                      <p className="text-xs text-red-700" role="alert">{errors.objectives}</p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="cf-additional">Additional information (optional)</Label>
                    <Textarea
                      id="cf-additional"
                      data-testid="contact-additional-input"
                      value={form.additional}
                      onChange={set("additional")}
                      className={areaCls}
                    />
                  </div>

                  <div className="hidden" aria-hidden="true">
                    <label htmlFor="cf-nickname">Leave this field empty</label>
                    <input
                      id="cf-nickname"
                      type="text"
                      tabIndex={-1}
                      autoComplete="off"
                      value={form.nickname}
                      onChange={set("nickname")}
                      data-testid="contact-honeypot"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={submitting}
                    data-testid="contact-submit-button"
                    className="group inline-flex items-center gap-3 bg-ink text-white px-9 py-4 text-sm tracking-wide transition-colors duration-300 hover:bg-mutedblue disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {submitting ? (
                      <>
                        Sending
                        <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                      </>
                    ) : (
                      <>
                        Send Enquiry
                        <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" aria-hidden="true" />
                      </>
                    )}
                  </button>
                </form>
              )}
            </FadeIn>
          </div>
        </div>
      </section>
    </>
  );
}
